import { ComponentChildren } from 'preact'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import ButtonToggle from '@bonniernews/dn-design-system-web/components/button-toggle/button-toggle.tsx'
import Checkbox from '@bonniernews/dn-design-system-web/components/checkbox/checkbox.tsx'
import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'
import { Switch } from '@bonniernews/dn-design-system-web/components/switch/switch.tsx'

import {
  ImageListItemProps,
  InnerListItemProps,
  ListItemProps,
  TitleProps,
  ToggleListItemProps,
} from './list-item-types.ts'

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/list-item](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/list-item)
 * - Storybook: [ListItem](https://designsystem.dn.se/?path=/docs/basic-listitem--docs)
 *
 * This component is used to render a list item with different types of content. This lists all possible properties. Some are restricted to certain list item types. Check the list item types for more information.
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/list-item/list-item.scss'`
 *
 * ## Javascript
 *
 * _The javascript is only needed for list items of type accordion or toggle_
 *
 * ```javascript
 * import dsListItem from '@bonniernews/dn-design-system-web/components/list-item/list-item.js'
 *
 * const listElements = Array.from(document.getElementsByClassName("ds-list-item"));
 * dsListItem(listElements);
 * ```
 *
 */
export const ListItem = (props: ListItemProps) => {
  const {
    listItemType,
    title,
    subtitle,
    meta,
    border = false,
    fontWeight = 'regular',
    attributes,
    linkAttributes,
    disabled,
    forcePx,
    classNames,
  } = props

  const componentClassName = 'ds-list-item'

  const classes = formatClassString([
    componentClassName,
    border && `${componentClassName}--border`,
    forcePx && 'ds-force-px',
    classNames,
  ])

  let iconLeftSvg
  if (listItemType === 'accordion' || listItemType === 'switch' || listItemType === 'standard') {
    const { leadingIcon } = props

    if (leadingIcon) {
      iconLeftSvg = IconUse({ iconName: leadingIcon })
    }
  }

  let iconRightSvg
  if (listItemType === 'standard' || listItemType === 'image') {
    const { trailingIcon } = props
    if (trailingIcon) {
      iconRightSvg = IconUse({ iconName: trailingIcon })
    }
  }

  const mediaHtml = listItemType === 'image' && (props as ImageListItemProps).mediaHtml

  return (
    <li className={classes}>
      <ListItemInner {...{ ...props, componentClassName }}>
        {iconLeftSvg && <span className={`${componentClassName}__icon-left`}>{iconLeftSvg}</span>}
        {mediaHtml && (
          <div className={`${componentClassName}__image`} dangerouslySetInnerHTML={{ __html: mediaHtml }}></div>
        )}
        {title && (
          <Title
            title={title}
            subtitle={subtitle}
            titleHref={(listItemType === 'toggle' && (props as ToggleListItemProps).titleHref) || undefined}
            fontWeight={fontWeight}
            componentClassName={componentClassName}
            disabled={disabled}
            attributes={attributes}
            linkAttributes={linkAttributes}
          />
        )}
        {meta && <span className={`${componentClassName}__meta`}>{meta}</span>}
        {iconRightSvg && <span className={`${componentClassName}__icon-right`}>{iconRightSvg}</span>}
      </ListItemInner>
    </li>
  )
}

const ListItemInner = ({
  listItemType,
  href,
  disabled,
  attributes,
  linkAttributes,
  interactiveElementAttributes,
  interactiveElementClassNames,
  checked,
  selected,
  variant,
  toggleText,
  toggleSelectedText,
  name = 'ds-list-item',
  forcePx,
  componentClassName,
  accordionContent,
  children,
}: InnerListItemProps) => {
  const classes = formatClassString([
    `${componentClassName}__content`,
    `${componentClassName}--${listItemType}`,
    disabled && `${componentClassName}--disabled`,
  ])

  switch (listItemType) {
    case 'standard':
    case 'image':
      const hasLink = href && !disabled

      return (
        <>
          {hasLink ? (
            <a className={classes} href={href} {...linkAttributes}>
              {children}
            </a>
          ) : (
            <button className={classes} disabled={disabled} {...attributes}>
              {children}
            </button>
          )}
        </>
      )
    case 'checkbox':
      return (
        <label className={classes} htmlFor={name} {...attributes}>
          <ListItemIconWrapper placement='left' componentClassName={componentClassName}>
            <Checkbox
              name={name}
              checked={checked}
              stripLabel={true}
              disabled={disabled}
              forcePx={forcePx}
              attributes={interactiveElementAttributes}
              classNames={interactiveElementClassNames}
            />
          </ListItemIconWrapper>
          {children}
        </label>
      )
    case 'switch':
      return (
        <label className={classes} htmlFor={name} {...attributes}>
          {children}

          <ListItemIconWrapper placement='right' componentClassName={componentClassName}>
            <Switch
              name={name}
              showMeta={true}
              checked={checked}
              disabled={disabled}
              forcePx={forcePx}
              stripLabel={true}
              attributes={interactiveElementAttributes}
              classNames={interactiveElementClassNames}
            />
          </ListItemIconWrapper>
        </label>
      )
    case 'accordion':
      return (
        <>
          <button className={classes} disabled={disabled} {...attributes}>
            {children}
            <ListItemIconWrapper placement='right' componentClassName={componentClassName}>
              <IconUse iconName={'expand_less'} />
              <IconUse iconName={'expand_more'} />
            </ListItemIconWrapper>
          </button>
          {accordionContent && <div className={`${componentClassName}__accordion-inner`}>{accordionContent}</div>}
        </>
      )
    case 'toggle':
      const buttonComponent = (
        <ButtonToggle
          text={toggleText}
          selectedText={toggleSelectedText || ''}
          variant={variant || 'secondaryFilled'}
          size='sm'
          selected={selected}
          disabled={disabled}
          forcePx={forcePx}
          attributes={interactiveElementAttributes}
          classNames={interactiveElementClassNames}
        />
      )

      return (
        <div className={classes} {...attributes}>
          {children}
          {buttonComponent}
        </div>
      )
    default:
      return (
        <div className={classes} {...attributes}>
          {children}
        </div>
      )
  }
}

const Title = ({
  title,
  subtitle,
  titleHref,
  fontWeight,
  disabled,
  attributes,
  linkAttributes,
  componentClassName,
}: TitleProps) => {
  const titleClasses = formatClassString([
    `${componentClassName}__title`,
    `${componentClassName}__title--${fontWeight}`,
  ])

  return (
    <div className={`${componentClassName}__titles`}>
      {titleHref && !disabled ? (
        <a class={titleClasses} href={titleHref} {...linkAttributes}>
          {title}
        </a>
      ) : (
        <span className={titleClasses} {...attributes}>
          {title}
        </span>
      )}
      {subtitle && <span className={`${componentClassName}__subtitle`}>{subtitle}</span>}
    </div>
  )
}

const ListItemIconWrapper = ({
  placement,
  componentClassName,
  children,
}: {
  placement: 'left' | 'right'
  componentClassName: string
  children?: ComponentChildren
}) => {
  return <div className={`${componentClassName}__icon-${placement}`}>{children}</div>
}
